import { getLogger } from '@/logger';
import { Task, TaskEvent, TaskEventCallback, TaskOptions } from '@/types/tasks';
import { TASK_PRIORITY } from './const';
import { StablePriorityQueue } from './StablePriorityQueue';

const logger = getLogger('TaskQueue');

/**
 * Priority-based function queue with concurrency limits, retries, timeouts, and debounced run.
 */
export class TaskQueue {
  private readonly queue: StablePriorityQueue<Task>;
  private runningCount: number;
  private readonly concurrencyLimit: number;
  private isPaused: boolean;
  private readonly maxQueueSize: number;
  private readonly eventListeners: Partial<Record<TaskEvent, TaskEventCallback[]>>;
  private runTimeout: NodeJS.Timeout | null = null;
  private nextTaskId: number;
  private nextPriority = TASK_PRIORITY.MID;
  private runDebounceTimeMs = 50;

  constructor(concurrencyLimit: number = 1, maxQueueSize: number = Infinity) {
    this.queue = new StablePriorityQueue<Task>((a, b) => a.priority - b.priority);
    this.runningCount = 0;
    this.concurrencyLimit = concurrencyLimit;
    this.isPaused = false;
    this.maxQueueSize = maxQueueSize;
    this.eventListeners = {};
    this.nextTaskId = 1;
  }

  enqueue({ fn, priority = 0, retries = 0, timeout = null, taskId = this.generateTaskId() }: TaskOptions): void {
    if (this.queue.size >= this.maxQueueSize) {
      logger.error("Queue is full. Can't add more tasks.");
      return;
    }

    const task: Task = {
      fn,
      priority: priority || this.nextPriority++,
      retries,
      maxRetries: retries,
      timeout,
      taskId,
    };

    this.queue.push(task);
    logger.debug('Task enqueued', task);
    if (!this.isPaused) {
      void this.run();
    }
  }

  async run() {
    if (this.isPaused || this.runningCount >= this.concurrencyLimit || this.queue.isEmpty()) return;

    if (this.runTimeout) return;
    this.runTimeout = setTimeout(async () => {
      this.runTimeout = null;

      while (this.runningCount < this.concurrencyLimit && !this.queue.isEmpty()) {
        this.runningCount++;
        const task = this.queue.pop();
        logger.debug('Running task', task, this.queue);
        if (task) {
          this.triggerEvent('taskStart', task);

          try {
            await this.executeTask(task);
            this.triggerEvent('taskComplete', task);
          } catch {
            this.triggerEvent('taskError', task);
          }
        }
        this.runningCount--;
        logger.debug('Task finished', task, this.queue);
      }

      await this.run();
    }, this.runDebounceTimeMs);
  }

  private async executeTask(task: Task) {
    let attempt = 0;
    while (attempt <= task.maxRetries) {
      attempt++;
      try {
        if (task.timeout) {
          await this.runWithTimeout(task.fn, task.timeout);
        } else {
          await task.fn();
        }
        break;
      } catch (error) {
        if (attempt > task.maxRetries) throw error;
      }
    }
  }

  private async runWithTimeout(fn: TaskOptions['fn'], timeout: number) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Task timed out'));
      }, timeout);

      fn()
        .then(result => {
          clearTimeout(timer);
          resolve(result);
        })
        .catch(error => {
          clearTimeout(timer);
          reject(error);
        });
    });
  }

  pause(): void {
    this.isPaused = true;
    logger.debug('queue processing paused');
  }

  /**
   * Resume the queue, allowing tasks to be processed again.
   */
  resume(): void {
    if (this.isPaused) {
      this.isPaused = false;
      logger.debug('queue processing resumed');
      void this.run();
    }
  }

  on(eventName: TaskEvent, callback: TaskEventCallback): void {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];
    }
    this.eventListeners[eventName].push(callback);
  }

  private triggerEvent(eventName: TaskEvent, task: Task): void {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach(callback => {
        callback(task);
      });
    }
  }

  getNextTaskId(): string | null {
    return this.queue.peek()?.taskId ?? null;
  }

  generateTaskId(name = 'task'): string {
    return `${name}-${this.nextTaskId++}`;
  }

  isEmpty() {
    return this.queue.isEmpty();
  }

  size() {
    return this.queue.size;
  }
}
